import { useEffect, useState } from "react";
import API, { formatApiError } from "../lib/api";
import { Switch } from "./ui/switch";
import { Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";

/* Per-job admin control: whether the client portal may reveal the assigned contractor's name.
   Unset → follows the client's default (Client detail page → "Show Contractor Names by Default"). */
export default function ClientVisibilityToggle({ entityType = "job", entityId, value, clientId, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [clientDefault, setClientDefault] = useState(null);
  const overridden = value === true || value === false;

  useEffect(() => {
    if (!clientId) { setClientDefault(false); return; }
    API.get(`/clients/${clientId}`).then(({ data }) => setClientDefault(!!data?.show_contractor_default)).catch(() => setClientDefault(false));
  }, [clientId]);

  const effective = overridden ? value : !!clientDefault;
  const path = `/${entityType === "emergency_job" ? "emergency-jobs" : entityType === "project" ? "projects" : entityType === "contract" ? "contracts" : "jobs"}/${entityId}`;
  const noun = entityType === "project" ? "project" : entityType === "contract" ? "contract" : "job";

  const save = async (next, msg) => {
    setBusy(true);
    try {
      await API.put(path, { show_contractor_to_client: next });
      toast.success(msg);
      onChanged?.(next);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not update"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-3 pt-3 border-t border-border flex items-start justify-between gap-3" data-testid="client-visibility-toggle" data-effective={effective ? "shown" : "hidden"} data-overridden={overridden ? "1" : "0"}>
      <div className="min-w-0">
        <p className="text-xs font-medium flex items-center gap-1.5">{effective ? <Eye size={13} className="text-emerald-600" /> : <EyeSlash size={13} className="text-muted-foreground" />} Show contractor to client</p>
        <p className="text-[10px] text-muted-foreground mt-0.5">
          {overridden
            ? (value ? `Override: the client portal names the assigned ${entityType === "contract" ? "team (in-house operative or supplier) on this contract and its visits" : `contractors for this ${noun}`}.` : `Override: hidden from the client for this ${noun}.`)
            : clientDefault === null ? "Following the client's default…"
            : clientDefault ? "Following client default — this client sees contractor names." : "Following client default — the portal shows “Contractor assigned” without naming them."}
        </p>
        {overridden && clientId && (
          <button type="button" className="text-[10px] text-primary hover:underline mt-1" disabled={busy}
                  onClick={() => save(null, "Back to the client's default")} data-testid="show-contractor-reset">Reset to client default</button>
        )}
      </div>
      <Switch checked={effective} disabled={busy || clientDefault === null} data-testid="show-contractor-switch"
              onCheckedChange={(next) => save(next, next ? "Client can now see the contractor's name" : "Contractor name hidden from client")} />
    </div>
  );
}
